import { useEffect, useMemo, useState } from "react";
import api from "../api";
import { toInputDate, toDate } from "../utils/dateUtils";

function TransfertSejourModal({
  data,
  onClose
}) {
  const [appartements, setAppartements] = useState([]);
  const [nouvelAppartementId, setNouvelAppartementId] = useState("");
  const [dateTransfert, setDateTransfert] = useState("");
  const [prixNuitee, setPrixNuitee] = useState("");
  const [motif, setMotif] = useState("");

  const [loadingAppartements, setLoadingAppartements] = useState(true);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // =========================================================
  // DATE DU JOUR
  // =========================================================

  const getToday = () => {
    return toInputDate(new Date());
  };

  // =========================================================
  // CHARGEMENT DES APPARTEMENTS
  // =========================================================

  useEffect(() => {
    const loadAppartements = async () => {
      try {
        setLoadingAppartements(true);
        setError("");

        const res = await api.get("/appartements");

        setAppartements(
          Array.isArray(res.data)
            ? res.data
            : []
        );
      } catch (err) {
        console.error(
          "ERREUR CHARGEMENT APPARTEMENTS TRANSFERT",
          err
        );

        setError(
          err?.response?.data?.error ||
          "Impossible de charger les appartements."
        );
      } finally {
        setLoadingAppartements(false);
      }
    };

    loadAppartements();
  }, []);

  // =========================================================
  // INITIALISATION DE LA DATE
  // =========================================================

  useEffect(() => {
    if (!data) return;

    /*
     * Pour un séjour en cours :
     * le transfert se fait normalement à partir de la date
     * choisie dans le modal.
     *
     * On préremplit avec aujourd'hui.
     */
    setDateTransfert(
      getToday()
    );
  }, [data]);

  // =========================================================
  // APPARTEMENT ACTUEL
  // =========================================================

  const appartementActuelId =
    Number(
      data?.appartement_id ??
      data?.appartement?.id ??
      0
    );

  const appartementActuelNumero =
    data?.appartement_numero ||
    data?.appartement?.numero ||
    "—";

  // =========================================================
  // APPARTEMENTS DISPONIBLES DANS LE SELECT
  // =========================================================

  const appartementsFiltres = useMemo(() => {
    return appartements.filter(
      (a) =>
        Number(a.id) !== appartementActuelId &&
        a.est_louable !== false
    );
  }, [
    appartements,
    appartementActuelId
  ]);

  // =========================================================
  // APPARTEMENT SÉLECTIONNÉ
  // =========================================================

  const appartementSelectionne = useMemo(() => {
    if (!nouvelAppartementId) {
      return null;
    }

    return appartements.find(
      (a) =>
        Number(a.id) ===
        Number(nouvelAppartementId)
    ) || null;
  }, [
    appartements,
    nouvelAppartementId
  ]);

  // =========================================================
  // PRIX AUTOMATIQUE DU NOUVEL APPARTEMENT
  // =========================================================

  useEffect(() => {
    if (!appartementSelectionne) {
      setPrixNuitee("");
      return;
    }

    setPrixNuitee(
      appartementSelectionne.prix_nuitee ??
      ""
    );
  }, [appartementSelectionne]);

  // =========================================================
  // DATE MINIMALE
  // =========================================================

  const dateFinSejour =
    data?.date_fin_prevue ||
    data?.date_fin ||
    null;

  const dateFinInput =
    dateFinSejour
      ? toInputDate(dateFinSejour)
      : "";

  // =========================================================
  // VALIDATION
  // =========================================================

  const validate = () => {
    if (!nouvelAppartementId) {
      setError(
        "Veuillez sélectionner le nouvel appartement."
      );
      return false;
    }

    if (
      Number(nouvelAppartementId) ===
      appartementActuelId
    ) {
      setError(
        "Le nouvel appartement doit être différent de l'appartement actuel."
      );
      return false;
    }

    if (!dateTransfert) {
      setError(
        "La date du transfert est requise."
      );
      return false;
    }

    if (
      dateFinInput &&
      toDate(dateTransfert) >=
        toDate(dateFinInput)
    ) {
      setError(
        "La date du transfert doit être antérieure à la date de fin du séjour."
      );
      return false;
    }

    if (
      prixNuitee === "" ||
      prixNuitee === null ||
      prixNuitee === undefined
    ) {
      setError(
        "Le prix de nuitée est requis."
      );
      return false;
    }

    const prix = Number(prixNuitee);

    if (
      !Number.isFinite(prix) ||
      prix < 0
    ) {
      setError(
        "Le prix de nuitée est invalide."
      );
      return false;
    }

    return true;
  };

  // =========================================================
  // TRANSFERT
  // =========================================================

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!validate()) {
      return;
    }

    const appartementNumero =
      appartementSelectionne?.numero ||
      "nouvel appartement";

    const confirmation = window.confirm(
      `Confirmer le transfert du séjour ?\n\n` +
      `Appartement actuel : ${appartementActuelNumero}\n` +
      `Nouvel appartement : ${appartementNumero}\n` +
      `Date du transfert : ${dateTransfert}\n` +
      `Prix de nuitée : ${prixNuitee} USD\n\n` +
      `Cette opération modifiera les affectations du séjour et recalculera la facture.`
    );

    if (!confirmation) {
      return;
    }

    try {
      setLoading(true);

      const response = await api.post(
        `/sejours/${data.id}/transfert`,
        {
          nouvel_appartement_id:
            Number(nouvelAppartementId),

          date_transfert:
            dateTransfert,

          prix_nuitee:
            Number(prixNuitee),

          motif:
            motif.trim() ||
            `Transfert vers ${appartementNumero}`
        }
      );

      console.log(
        "TRANSFERT RÉUSSI :",
        response.data
      );

      setSuccess(
        response?.data?.message ||
        "Séjour transféré avec succès."
      );

      /*
       * Petit délai pour permettre à l'utilisateur
       * de voir la confirmation avant fermeture.
       */
      setTimeout(() => {
        onClose?.();
      }, 700);

    } catch (err) {
      console.error(
        "ERREUR TRANSFERT SEJOUR :",
        err
      );

      console.error(
        "REPONSE SERVEUR :",
        err?.response?.data
      );

      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Erreur lors du transfert du séjour."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // AFFICHAGE
  // =========================================================

  return (
    <div className="w-full max-w-2xl">

      {/* =====================================================
          EN-TÊTE ROUGE
      ====================================================== */}

      <div className="bg-red-600 text-white px-5 py-4 rounded-t-xl">

        <div className="flex items-center justify-between">

          <div>
            <h2 className="text-xl font-bold">
              🔄 Transférer le séjour
            </h2>

            <p className="text-sm text-red-100 mt-1">
              Déplacer le séjour vers un autre appartement
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="text-white text-2xl leading-none hover:text-red-200"
          >
            ×
          </button>

        </div>

      </div>

      {/* =====================================================
          CORPS
      ====================================================== */}

      <div className="bg-white px-5 py-5 space-y-5">

        {/* ===================================================
            INFORMATION SÉJOUR
        ==================================================== */}

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">

          <div className="text-xs font-semibold text-gray-500 uppercase mb-3">
            Séjour actuel
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

            <div>
              <div className="text-xs text-gray-500">
                Appartement actuel
              </div>

              <div className="font-semibold text-gray-900">
                {appartementActuelNumero}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">
                Date d'arrivée
              </div>

              <div className="font-semibold text-gray-900">
                {data?.date_debut
                  ? toInputDate(data.date_debut)
                  : "—"}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500">
                Date de fin prévue
              </div>

              <div className="font-semibold text-gray-900">
                {dateFinInput || "—"}
              </div>
            </div>

          </div>

        </div>

        {/* ===================================================
            NOUVEL APPARTEMENT
        ==================================================== */}

        <div>

          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Nouvel appartement
          </label>

          {loadingAppartements ? (

            <div className="border rounded-lg px-4 py-3 text-gray-500">
              Chargement des appartements...
            </div>

          ) : (

            <select
              value={nouvelAppartementId}
              onChange={(e) => {
                setNouvelAppartementId(
                  e.target.value
                );
                setError("");
              }}
              disabled={loading}
              className="w-full border border-gray-300 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-red-500"
            >

              <option value="">
                -- Sélectionner un appartement --
              </option>

              {appartementsFiltres.map(
                (a) => (
                  <option
                    key={a.id}
                    value={a.id}
                  >
                    {a.numero}
                    {a.prix_nuitee !==
                      undefined &&
                      a.prix_nuitee !== null
                      ? ` — ${a.prix_nuitee} USD/nuit`
                      : ""}
                  </option>
                )
              )}

            </select>

          )}

          {appartementSelectionne && (
            <div className="mt-2 text-sm text-gray-600">

              Appartement sélectionné :
              <strong className="ml-1 text-gray-900">
                {appartementSelectionne.numero}
              </strong>

            </div>
          )}

        </div>

        {/* ===================================================
            DATE DU TRANSFERT
        ==================================================== */}

        <div>

          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Date du transfert
          </label>

          <input
            type="date"
            value={dateTransfert}
            min={getToday()}
            max={
              dateFinInput
                ? (() => {
                    const d =
                      toDate(dateFinInput);

                    d.setDate(
                      d.getDate() - 1
                    );

                    return toInputDate(d);
                  })()
                : undefined
            }
            onChange={(e) => {
              setDateTransfert(
                e.target.value
              );
              setError("");
            }}
            disabled={loading}
            className="w-full border border-gray-300 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-red-500"
          />

          <p className="text-xs text-gray-500 mt-1">
            Le transfert prend effet à partir de cette date.
          </p>

        </div>

        {/* ===================================================
            NOUVEAU PRIX
        ==================================================== */}

        <div>

          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Nouveau prix de nuitée
          </label>

          <div className="relative">

            <input
              type="number"
              min="0"
              step="0.01"
              value={prixNuitee}
              onChange={(e) => {
                setPrixNuitee(
                  e.target.value
                );
                setError("");
              }}
              disabled={
                loading ||
                !appartementSelectionne
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-3 pr-16 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Prix par nuit"
            />

            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
              USD
            </span>

          </div>

          {appartementSelectionne && (
            <p className="text-xs text-gray-500 mt-1">
              Prix standard de{" "}
              <strong>
                {appartementSelectionne.numero}
              </strong>
              :{" "}
              <strong>
                {appartementSelectionne.prix_nuitee ??
                  0}{" "}
                USD/nuit
              </strong>
            </p>
          )}

        </div>

        {/* ===================================================
            MOTIF
        ==================================================== */}

        <div>

          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Motif du transfert
            <span className="font-normal text-gray-400 ml-1">
              (facultatif)
            </span>
          </label>

          <textarea
            value={motif}
            onChange={(e) =>
              setMotif(e.target.value)
            }
            disabled={loading}
            rows={3}
            placeholder="Ex. Changement d'appartement demandé par le client..."
            className="w-full border border-gray-300 rounded-lg px-3 py-3 resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
          />

        </div>

        {/* ===================================================
            AVERTISSEMENT
        ==================================================== */}

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">

          <div className="font-semibold text-red-800 mb-1">
            ⚠️ Attention
          </div>

          <p className="text-sm text-red-700">
            Le transfert modifie l'affectation du séjour
            et recalcule automatiquement son montant ainsi
            que les lignes d'hébergement de la facture.
          </p>

        </div>

        {/* ===================================================
            ERREUR
        ==================================================== */}

        {error && (

          <div className="bg-red-50 border border-red-300 text-red-700 rounded-lg px-4 py-3 text-sm">
            <strong>Erreur :</strong>{" "}
            {error}
          </div>

        )}

        {/* ===================================================
            SUCCÈS
        ==================================================== */}

        {success && (

          <div className="bg-green-50 border border-green-300 text-green-700 rounded-lg px-4 py-3 text-sm">
            <strong>✓</strong>{" "}
            {success}
          </div>

        )}

        {/* ===================================================
            BOUTONS
        ==================================================== */}

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-5 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              loading ||
              loadingAppartements ||
              !nouvelAppartementId
            }
            className="px-5 py-3 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading
              ? "Transfert en cours..."
              : "🔄 Confirmer le transfert"}
          </button>

        </div>

      </div>

    </div>
  );
}

export default TransfertSejourModal;